import { useState } from 'react'
import { Shield, Sparkles, ArrowRight, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { KinkUpLogo } from '@/components/brand/KinkUpLogo'
import { cn } from '@/lib/utils'

interface OnboardingFlowProps {
  onComplete: () => void
}

const STEPS = [
  {
    icon: Sparkles, // overridden by KinkUpLogo in render
    title: 'Welcome to KinkUp',
    subtitle: 'Level up your dynamic',
    description: 'Manage tasks, earn rewards, and deepen your connection — all in a safe, encrypted environment designed for trust.',
    accent: 'primary',
  },
  {
    icon: Shield,
    title: 'Private & Secure',
    subtitle: 'Your data stays between you',
    description: 'Complete isolation between relationships. Doms never see other doms. Subs never see other subs. Your bond is yours alone.',
    accent: 'secondary',
  },
  {
    icon: Sparkles,
    title: 'Tasks & Rewards',
    subtitle: 'Structure meets spontaneity',
    description: 'Create tasks with spice levels, upload proof, verify completions, and earn points. Track everything with your personal calendar.',
    accent: 'primary',
  },
] as const

export function OnboardingFlow_M10_32({ onComplete }: OnboardingFlowProps) {
  const [step, setStep] = useState(0)
  const current = STEPS[step]
  const isLast = step === STEPS.length - 1

  function handleNext() {
    if (isLast) {
      onComplete()
    } else {
      setStep((s) => s + 1)
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6">
      {/* Background orbs */}
      <div className="absolute inset-0 bg-background" />
      <div
        className={cn(
          'absolute h-[400px] w-[400px] rounded-full blur-[120px] transition-all duration-1000',
          step === 0 && 'top-[-10%] left-[-10%] bg-primary/10',
          step === 1 && 'top-[20%] right-[-10%] bg-secondary/8',
          step === 2 && 'bottom-[-10%] left-[20%] bg-primary/10',
        )}
      />

      <div className="relative z-10 w-full max-w-sm">
        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 mb-12">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-1 rounded-full transition-all duration-500',
                i === step ? 'w-8 bg-primary' : i < step ? 'w-4 bg-primary/40' : 'w-4 bg-muted'
              )}
            />
          ))}
        </div>

        {/* Content — keyed to re-trigger animation on step change */}
        <div key={step} className="text-center animate-fade-in">
          {/* Icon — KinkUp logo on welcome step, Lucide icons on others */}
          <div
            className={cn(
              'inline-flex items-center justify-center h-20 w-20 rounded-3xl mb-8 shadow-glass',
              current.accent === 'primary'
                ? 'bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20'
                : 'bg-gradient-to-br from-secondary/20 to-secondary/5 border border-secondary/20'
            )}
          >
            {step === 0 ? (
              <KinkUpLogo size={40} animated className="animate-float" />
            ) : (
              <current.icon
                className={cn(
                  'h-9 w-9 animate-float',
                  current.accent === 'primary' ? 'text-primary' : 'text-secondary'
                )}
              />
            )}
          </div>

          {/* Text */}
          <h1 className="font-display text-3xl font-semibold tracking-tight mb-2 text-gradient">
            {current.title}
          </h1>
          <p className="text-sm font-medium text-foreground/80 mb-4">
            {current.subtitle}
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
            {current.description}
          </p>
        </div>

        {/* Actions */}
        <div className="mt-12 space-y-3">
          <Button
            onClick={handleNext}
            className={cn(
              'w-full h-12 text-sm font-medium shadow-glow-sm transition-all duration-300 group',
              isLast
                ? 'bg-gradient-to-r from-primary to-secondary hover:opacity-90'
                : 'bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70'
            )}
          >
            {isLast ? (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Get Started
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </>
            )}
          </Button>

          {!isLast && (
            <button
              onClick={onComplete}
              className="w-full py-2 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
            >
              Skip introduction
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
